'use client';

import { useRef, type ReactNode } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import styles from './OverlapRevealStack.module.css';

/**
 * Desktop: Ring AIR slides over a sticky Ring PRO. Measures stays in
 * normal flow. No pin when AIR is absent (US). Mobile stays sequential.
 */
export const OverlapRevealStack = ({ children }: { children: ReactNode }) => {
  const stageRef = useRef<HTMLDivElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const layers = Array.from(
      stage.querySelectorAll<HTMLElement>('[data-overlap-layer]'),
    );
    if (!layers.length) return undefined;

    layers.forEach((el) => el.removeAttribute('data-overlap-pin'));
    const pro = stage.querySelector<HTMLElement>('[data-overlap-layer="pro"]');
    const air = stage.querySelector<HTMLElement>('[data-overlap-layer="air"]');
    if (pro && air) pro.setAttribute('data-overlap-pin', '');

    const setHeight = (el: HTMLElement) => {
      el.style.setProperty('--overlap-h', `${el.offsetHeight}px`);
    };

    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => setHeight(entry.target as HTMLElement));
    });
    layers.forEach((el) => {
      setHeight(el);
      ro.observe(el);
    });

    return () => {
      ro.disconnect();
      layers.forEach((el) => {
        el.removeAttribute('data-overlap-pin');
        el.style.removeProperty('--overlap-h');
      });
    };
  }, []);

  return (
    <div ref={stageRef} className={styles.stage}>
      {children}
    </div>
  );
};

export default OverlapRevealStack;
